/**
 * B2BCorporatePage — HO / Chief Accountant backoffice module for corporate B2B orders.
 * Tabs:
 *  1) Clients       — client master with bulk pricing
 *  2) Orders        — manual PO-based order entry + list
 *  3) Dispatch      — link mfg batches + generate dispatch note PDF
 *  4) Invoices      — GST tax invoice (converted from order) + PDF
 *  5) Client Ledger — per-client A/R ledger with outstanding
 */
import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ColorTab } from "@/components/ui/color-tab";
import { inr, fmtDate } from "@/lib/utils-format";
import { Building2, Plus, Pencil, Trash2, ShoppingCart, Truck, FileText, BookOpen, Download, RefreshCw, Package, Search, ArrowRight } from "lucide-react";

const ALLOWED_ROLES = ["super_admin", "admin", "chief_accountant", "ho_admin", "accountant"];

export default function B2BCorporatePage() {
  const { user } = useAuth();
  const [tab, setTab] = useState("clients");
  if (user && !ALLOWED_ROLES.includes(user.role)) return <Navigate to="/app/dashboard" replace />;

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-4" data-testid="b2b-corporate-page">
      <div>
        <div className="text-[11px] font-mono tracking-widest uppercase text-[#C05621]">Corporate B2B</div>
        <h1 className="font-[Outfit] text-3xl font-semibold text-[#2C1810] flex items-center gap-2 mt-1">
          <Building2 className="w-7 h-7 text-[#C05621]" /> B2B Order &amp; Billing
        </h1>
        <p className="text-sm text-[#8B7355] mt-1">
          Manage corporate accounts (Reliance, Lulu, distributors) — PO-based orders, dispatch tracking with manufacturing batches, GST tax invoicing, and per-client A/R ledger.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-transparent p-0 gap-2 h-auto flex-wrap">
          <ColorTab value="clients" tone="orange" icon={Building2} testid="b2b-tab-clients">Clients</ColorTab>
          <ColorTab value="orders" tone="blue" icon={ShoppingCart} testid="b2b-tab-orders">Orders</ColorTab>
          <ColorTab value="dispatch" tone="amber" icon={Truck} testid="b2b-tab-dispatch">Dispatch</ColorTab>
          <ColorTab value="invoices" tone="green" icon={FileText} testid="b2b-tab-invoices">Invoices</ColorTab>
          <ColorTab value="ledger" tone="slate" icon={BookOpen} testid="b2b-tab-ledger">Client Ledger</ColorTab>
        </TabsList>

        <TabsContent value="clients" className="pt-4"><ClientsTab /></TabsContent>
        <TabsContent value="orders" className="pt-4"><OrdersTab onGoDispatch={() => setTab("dispatch")} onGoInvoice={() => setTab("invoices")} /></TabsContent>
        <TabsContent value="dispatch" className="pt-4"><DispatchTab /></TabsContent>
        <TabsContent value="invoices" className="pt-4"><InvoicesTab /></TabsContent>
        <TabsContent value="ledger" className="pt-4"><LedgerTab /></TabsContent>
      </Tabs>
    </div>
  );
}

/* ============================================================
 *  1) CLIENTS TAB — Corporate Client Master with Bulk Pricing
 * ============================================================ */
const emptyClient = {
  name: "", company: "", gstin: "", email: "", phone: "",
  address_line1: "", address_line2: "", city: "", state: "", pincode: "",
  customer_type: "b2b", credit_limit: 0, notes: "",
};

function ClientsTab() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState(null);   // { mode: 'create'|'edit', client }
  const [pricingFor, setPricingFor] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { setClients((await api.get("/b2b-corporate/clients")).data); }
    catch { toast.error("Failed to load clients"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return clients;
    return clients.filter((c) => (c.company || c.name || "").toLowerCase().includes(ql) || (c.gstin || "").toLowerCase().includes(ql));
  }, [clients, q]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#8B7355]" />
          <Input className="pl-7 w-72 bg-white" placeholder="Search company or GSTIN…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="b2b-client-search" />
        </div>
        <Button variant="outline" onClick={load} disabled={loading}><RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh</Button>
        <div className="ml-auto">
          <Button className="bg-[#C05621] hover:bg-[#9C4221] text-white" onClick={() => setDialog({ mode: "create", client: { ...emptyClient } })} data-testid="b2b-client-add">
            <Plus className="w-4 h-4 mr-1" /> New Corporate Client
          </Button>
        </div>
      </div>

      <Card className="border-[#EAE5D9]">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-xs uppercase tracking-wider text-[#8B7355]">
              <tr>
                <th className="text-left px-4 py-2.5">Company</th>
                <th className="text-left px-4 py-2.5">GSTIN</th>
                <th className="text-left px-4 py-2.5">Contact</th>
                <th className="text-left px-4 py-2.5">City / State</th>
                <th className="text-left px-4 py-2.5">Type</th>
                <th className="text-right px-4 py-2.5">Credit Limit</th>
                <th className="text-center px-4 py-2.5">Bulk Prices</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="b2b-client-rows">
              {filtered.map((c) => (
                <tr key={c.id} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7]" data-testid={`b2b-client-row-${c.id}`}>
                  <td className="px-4 py-2 font-medium text-[#2C1810]">{c.company || c.name}<div className="text-[10px] text-[#8B7355]">{c.name}</div></td>
                  <td className="px-4 py-2 font-mono text-xs">{c.gstin || "—"}</td>
                  <td className="px-4 py-2 text-xs">{c.email || "—"}<div className="text-[#8B7355]">{c.phone}</div></td>
                  <td className="px-4 py-2 text-xs">{[c.city, c.state].filter(Boolean).join(", ") || "—"}</td>
                  <td className="px-4 py-2"><Badge variant="outline" className="uppercase text-[10px]">{c.customer_type}</Badge></td>
                  <td className="px-4 py-2 text-right font-mono text-xs">{inr(c.credit_limit || 0)}</td>
                  <td className="px-4 py-2 text-center">
                    <Button size="sm" variant="outline" onClick={() => setPricingFor(c)} data-testid={`b2b-client-pricing-${c.id}`}>
                      {(c.bulk_pricing || []).length} <Package className="w-3 h-3 ml-1" />
                    </Button>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => setDialog({ mode: "edit", client: { ...c } })} className="p-1 hover:bg-[#FEF9EA] rounded text-[#C05621]" data-testid={`b2b-client-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-[#8B7355]"><Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />No corporate clients yet</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {dialog && <ClientDialog dialog={dialog} onClose={() => setDialog(null)} onSaved={() => { setDialog(null); load(); }} />}
      {pricingFor && <BulkPricingDialog client={pricingFor} onClose={() => setPricingFor(null)} onSaved={() => { setPricingFor(null); load(); }} />}
    </div>
  );
}

function ClientDialog({ dialog, onClose, onSaved }) {
  const [form, setForm] = useState(dialog.client);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name?.trim()) return toast.error("Contact name is required");
    if (!form.company?.trim()) return toast.error("Company name is required");
    setSaving(true);
    try {
      if (dialog.mode === "create") await api.post("/b2b-corporate/clients", form);
      else await api.patch(`/b2b-corporate/clients/${dialog.client.id}`, form);
      toast.success(`Client ${dialog.mode === "create" ? "created" : "updated"}`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">{dialog.mode === "create" ? "New Corporate Client" : "Edit Client"}</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Capture the corporate account details including GSTIN and credit limit. Bulk pricing can be configured after saving.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="col-span-2"><Label>Company Name *</Label><Input value={form.company || ""} onChange={(e) => set("company", e.target.value)} placeholder="e.g. Reliance Retail Ltd." data-testid="b2b-client-form-company" /></div>
          <div><Label>Contact Person *</Label><Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="b2b-client-form-name" /></div>
          <div><Label>GSTIN</Label><Input value={form.gstin || ""} onChange={(e) => set("gstin", e.target.value.toUpperCase())} placeholder="27AABCR1234A1Z5" className="font-mono" data-testid="b2b-client-form-gstin" /></div>
          <div><Label>Email</Label><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} data-testid="b2b-client-form-email" /></div>
          <div><Label>Phone</Label><Input value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
          <div className="col-span-2"><Label>Address Line 1</Label><Input value={form.address_line1 || ""} onChange={(e) => set("address_line1", e.target.value)} /></div>
          <div className="col-span-2"><Label>Address Line 2</Label><Input value={form.address_line2 || ""} onChange={(e) => set("address_line2", e.target.value)} /></div>
          <div><Label>City</Label><Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} /></div>
          <div><Label>State</Label><Input value={form.state || ""} onChange={(e) => set("state", e.target.value)} /></div>
          <div><Label>Pincode</Label><Input value={form.pincode || ""} onChange={(e) => set("pincode", e.target.value)} /></div>
          <div>
            <Label>Client Type</Label>
            <Select value={form.customer_type || "b2b"} onValueChange={(v) => set("customer_type", v)}>
              <SelectTrigger className="bg-white"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="b2b">B2B (Corporate)</SelectItem>
                <SelectItem value="wholesale">Wholesale</SelectItem>
                <SelectItem value="distributor">Distributor</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Credit Limit (₹)</Label><Input type="number" value={form.credit_limit || 0} onChange={(e) => set("credit_limit", Number(e.target.value))} /></div>
          <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="b2b-client-form-save">
            {saving ? "Saving…" : "Save Client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkPricingDialog({ client, onClose, onSaved }) {
  const [items, setItems] = useState([]);
  const [priceList, setPriceList] = useState([]);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const pl = (await api.get(`/b2b-corporate/clients/${client.id}/price-list`)).data;
      setPriceList(pl.items);
      // Existing overrides from client.bulk_pricing
      const byPid = Object.fromEntries((client.bulk_pricing || []).map((r) => [r.product_id, r]));
      setItems(pl.items.map((p) => ({
        product_id: p.product_id, sku: p.sku, name: p.name, unit: p.unit,
        default_price: p.default_price, hsn: p.hsn,
        gst_rate: p.gst_rate,
        override_price: byPid[p.product_id]?.unit_price ?? "",
      })));
    })().catch(() => toast.error("Failed to load price-list"));
  }, [client]);

  const filtered = useMemo(() => {
    const ql = q.trim().toLowerCase();
    if (!ql) return items;
    return items.filter((it) => (it.name || "").toLowerCase().includes(ql) || (it.sku || "").toLowerCase().includes(ql));
  }, [items, q]);

  const save = async () => {
    setSaving(true);
    try {
      const bulk_pricing = items
        .filter((it) => it.override_price !== "" && Number(it.override_price) > 0)
        .map((it) => ({ product_id: it.product_id, sku: it.sku, name: it.name, unit_price: Number(it.override_price), gst_rate: it.gst_rate }));
      await api.patch(`/b2b-corporate/clients/${client.id}`, { bulk_pricing });
      toast.success(`Saved ${bulk_pricing.length} bulk price entries`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const set = (pid, price) => setItems((prev) => prev.map((it) => it.product_id === pid ? { ...it, override_price: price } : it));

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-4xl max-h-[85vh]">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Bulk Pricing — {client.company || client.name}</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Enter a custom rate per product. Leave blank to fall back to default price. Rates are exclusive/inclusive as configured per product.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 mb-2">
          <div className="relative flex-1"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-[#8B7355]" /><Input className="pl-7 bg-white" placeholder="Filter products…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="b2b-pricing-search" /></div>
          <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">
            {items.filter((it) => it.override_price !== "" && Number(it.override_price) > 0).length} overrides
          </Badge>
        </div>
        <div className="overflow-y-auto max-h-[55vh] border border-[#EAE5D9] rounded">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-xs uppercase text-[#8B7355] sticky top-0">
              <tr>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-right px-3 py-2">Default ₹</th>
                <th className="text-right px-3 py-2">Bulk ₹ (override)</th>
                <th className="text-right px-3 py-2">Discount</th>
              </tr>
            </thead>
            <tbody data-testid="b2b-pricing-rows">
              {filtered.map((it) => {
                const disc = it.override_price !== "" && Number(it.override_price) > 0
                  ? ((it.default_price - Number(it.override_price)) / (it.default_price || 1) * 100).toFixed(1)
                  : null;
                return (
                  <tr key={it.product_id} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7]">
                    <td className="px-3 py-1.5">{it.name} <span className="text-[10px] text-[#8B7355]">{it.hsn && `HSN ${it.hsn}`}</span></td>
                    <td className="px-3 py-1.5 font-mono text-xs text-[#8B7355]">{it.sku || "—"}</td>
                    <td className="px-3 py-1.5 text-right font-mono text-xs">{inr(it.default_price)}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Input type="number" min="0" step="0.01" value={it.override_price} onChange={(e) => set(it.product_id, e.target.value)} className="w-24 h-8 text-right ml-auto bg-white" placeholder="—" data-testid={`b2b-pricing-input-${it.product_id}`} />
                    </td>
                    <td className="px-3 py-1.5 text-right text-xs">
                      {disc !== null && Number(disc) > 0 && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">-{disc}%</Badge>}
                      {disc !== null && Number(disc) < 0 && <Badge className="bg-red-100 text-red-800 border-red-200">+{Math.abs(disc)}%</Badge>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="b2b-pricing-save">{saving ? "Saving…" : "Save Bulk Prices"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
 *  2) ORDERS TAB — Manual PO Entry + List
 * ============================================================ */
function OrdersTab({ onGoDispatch, onGoInvoice }) {
  const [orders, setOrders] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = async () => {
    try { setOrders((await api.get("/b2b-corporate/orders")).data); }
    catch { toast.error("Failed to load orders"); }
  };
  useEffect(() => { load(); }, []);

  const STATUS_TONE = {
    confirmed: "bg-blue-100 text-blue-800 border-blue-200",
    dispatched: "bg-amber-100 text-amber-800 border-amber-200",
    invoiced: "bg-emerald-100 text-emerald-800 border-emerald-200",
  };
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#8B7355]">Enter a new sales order from a customer's Purchase Order (PO). Bulk-priced items load automatically from the client master.</p>
        <Button className="bg-[#C05621] hover:bg-[#9C4221] text-white" onClick={() => setShowNew(true)} data-testid="b2b-order-add"><Plus className="w-4 h-4 mr-1" /> New B2B Order</Button>
      </div>
      <Card className="border-[#EAE5D9]">
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-xs uppercase tracking-wider text-[#8B7355]">
              <tr>
                <th className="text-left px-4 py-2.5">Order #</th>
                <th className="text-left px-4 py-2.5">Date</th>
                <th className="text-left px-4 py-2.5">Client</th>
                <th className="text-left px-4 py-2.5">Customer PO</th>
                <th className="text-right px-4 py-2.5">Total (₹)</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="b2b-order-rows">
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-[#EAE5D9] hover:bg-[#FDFBF7]" data-testid={`b2b-order-row-${o.id}`}>
                  <td className="px-4 py-2 font-mono text-xs font-semibold">{o.number}</td>
                  <td className="px-4 py-2 text-xs">{fmtDate(o.created_at)}</td>
                  <td className="px-4 py-2">{o.customer_snapshot?.company || o.customer_snapshot?.name}</td>
                  <td className="px-4 py-2 font-mono text-xs">{o.customer_po_number}</td>
                  <td className="px-4 py-2 text-right font-mono">{inr(o.grand_total)}</td>
                  <td className="px-4 py-2"><Badge variant="outline" className={`uppercase text-[10px] ${STATUS_TONE[o.status] || ""}`}>{o.status}</Badge></td>
                  <td className="px-4 py-2 text-right">
                    {o.status === "confirmed" && <Button size="sm" variant="outline" onClick={onGoDispatch}><Truck className="w-3 h-3 mr-1" /> Dispatch</Button>}
                    {o.status === "dispatched" && <Button size="sm" variant="outline" onClick={onGoInvoice}><FileText className="w-3 h-3 mr-1" /> Invoice</Button>}
                    {o.invoice_number && <Badge className="bg-emerald-600 text-white border-0 ml-1">{o.invoice_number}</Badge>}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8B7355]"><ShoppingCart className="w-8 h-8 mx-auto mb-2 opacity-40" />No B2B orders yet</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {showNew && <NewOrderDialog onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewOrderDialog({ onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [pl, setPl] = useState([]);
  const [items, setItems] = useState([]);
  const [poNumber, setPoNumber] = useState("");
  const [poDate, setPoDate] = useState("");
  const [interstate, setInterstate] = useState(false);
  const [terms, setTerms] = useState({ delivery: "", payment: "Net 30" });
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/b2b-corporate/clients").then((r) => setClients(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!clientId) { setPl([]); return; }
    api.get(`/b2b-corporate/clients/${clientId}/price-list`).then((r) => setPl(r.data.items)).catch(() => setPl([]));
    setItems([]);
  }, [clientId]);

  const addItem = (product_id) => {
    const p = pl.find((x) => x.product_id === product_id);
    if (!p) return;
    if (items.find((it) => it.product_id === product_id)) return toast.error("Already added");
    setItems((prev) => [...prev, {
      product_id: p.product_id, name: p.name, quantity: 1, unit: p.unit || "pcs",
      unit_price: p.effective_price, gst_rate: p.gst_rate, hsn: p.hsn,
    }]);
  };
  const upd = (i, k, v) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rm = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const total = useMemo(() => items.reduce((s, it) => {
    const g = Number(it.quantity || 0) * Number(it.unit_price || 0);
    return s + g + (g * (Number(it.gst_rate || 0) / 100));
  }, 0), [items]);

  const save = async () => {
    if (!clientId) return toast.error("Choose a client");
    if (!poNumber.trim()) return toast.error("Customer PO Number is required");
    if (!items.length) return toast.error("Add at least one item");
    setSaving(true);
    try {
      await api.post("/b2b-corporate/orders", {
        customer_id: clientId,
        customer_po_number: poNumber.trim(),
        customer_po_date: poDate || null,
        is_interstate: interstate,
        delivery_terms: terms.delivery, payment_terms: terms.payment,
        notes,
        items,
      });
      toast.success("B2B order created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const bulkOverrides = items.filter((it) => {
    const p = pl.find((x) => x.product_id === it.product_id);
    return p && p.has_override;
  }).length;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">New B2B Sales Order</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Enter a sales order manually from a customer's Purchase Order. Bulk-priced items load automatically from the client master.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Corporate Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="bg-white" data-testid="b2b-order-client"><SelectValue placeholder="Pick client" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Customer PO # *</Label>
            <Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="REL/PO/2026/000123" data-testid="b2b-order-po" />
          </div>
          <div>
            <Label>PO Date</Label>
            <Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} className="bg-white" />
          </div>
          <div>
            <Label>Delivery Terms</Label>
            <Input value={terms.delivery} onChange={(e) => setTerms({ ...terms, delivery: e.target.value })} placeholder="FOR Warehouse Mumbai" />
          </div>
          <div>
            <Label>Payment Terms</Label>
            <Input value={terms.payment} onChange={(e) => setTerms({ ...terms, payment: e.target.value })} />
          </div>
          <div className="flex items-end gap-3 pb-1">
            <Switch checked={interstate} onCheckedChange={setInterstate} id="ints" />
            <Label htmlFor="ints" className="text-sm">Interstate (IGST)</Label>
          </div>
        </div>

        {clientId && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <Label>Add Line Items</Label>
              {bulkOverrides > 0 && <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">✓ {bulkOverrides} using bulk price</Badge>}
            </div>
            <Select value="" onValueChange={addItem}>
              <SelectTrigger className="bg-white" data-testid="b2b-order-add-item"><SelectValue placeholder="Search & add product…" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {pl.map((p) => (
                  <SelectItem key={p.product_id} value={p.product_id}>
                    {p.name} · ₹{p.effective_price}{p.has_override ? " (bulk)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="mt-2 border border-[#EAE5D9] rounded max-h-56 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF6EE] text-xs uppercase text-[#8B7355] sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">Product</th>
                    <th className="text-right px-2 py-1.5">Qty</th>
                    <th className="text-right px-2 py-1.5">Rate</th>
                    <th className="text-right px-2 py-1.5">GST %</th>
                    <th className="text-right px-2 py-1.5">Amount</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody data-testid="b2b-order-items">
                  {items.map((it, i) => (
                    <tr key={i} className="border-t border-[#EAE5D9]">
                      <td className="px-2 py-1">{it.name}</td>
                      <td className="px-2 py-1"><Input type="number" min="0" step="0.01" value={it.quantity} onChange={(e) => upd(i, "quantity", Number(e.target.value))} className="w-20 h-7 text-right ml-auto" /></td>
                      <td className="px-2 py-1"><Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => upd(i, "unit_price", Number(e.target.value))} className="w-24 h-7 text-right ml-auto" /></td>
                      <td className="px-2 py-1"><Input type="number" min="0" step="0.1" value={it.gst_rate} onChange={(e) => upd(i, "gst_rate", Number(e.target.value))} className="w-16 h-7 text-right ml-auto" /></td>
                      <td className="px-2 py-1 text-right font-mono">{inr(Number(it.quantity) * Number(it.unit_price) * (1 + Number(it.gst_rate) / 100))}</td>
                      <td className="px-2 py-1 text-right"><button onClick={() => rm(i)} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-[#8B7355] italic text-xs">No items</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="mt-2"><Label>Notes / Internal Remarks</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <div className="pt-2 flex items-center justify-between border-t border-[#EAE5D9] mt-2">
          <span className="text-sm text-[#8B7355]">Estimated Total (incl. GST):</span>
          <span className="text-2xl font-[Outfit] font-bold text-[#C05621]">{inr(total)}</span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="b2b-order-save">
            {saving ? "Saving…" : "Create Order"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
 *  3) DISPATCH TAB — Link Batches + Dispatch Note
 * ============================================================ */
function DispatchTab() {
  const [orders, setOrders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [showDispatch, setShowDispatch] = useState(null);   // order-based dispatch
  const [showStandalone, setShowStandalone] = useState(false);

  const load = async () => {
    try {
      const [os, ns] = await Promise.all([
        api.get("/b2b-corporate/orders"),
        api.get("/b2b-corporate/dispatch-notes"),
      ]);
      setOrders(os.data.filter((o) => o.status === "confirmed" || o.status === "dispatched"));
      setNotes(ns.data);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const dl = (oid) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/b2b-corporate/orders/${oid}/dispatch-note.pdf`, "_blank");
  const dlNote = (nid) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/b2b-corporate/dispatch-notes/${nid}/pdf`, "_blank");

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#8B7355]">Link one or more manufacturing batches to a confirmed order OR create a standalone Dispatch Note without an order.</p>
        <Button className="bg-[#C05621] hover:bg-[#9C4221] text-white" onClick={() => setShowStandalone(true)} data-testid="b2b-dispatch-standalone-add">
          <Plus className="w-4 h-4 mr-1" /> New Dispatch Note
        </Button>
      </div>

      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2"><CardTitle className="font-[Outfit] text-base">From Orders</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-xs uppercase text-[#8B7355]">
              <tr>
                <th className="text-left px-4 py-2.5">Order #</th>
                <th className="text-left px-4 py-2.5">Client</th>
                <th className="text-left px-4 py-2.5">PO #</th>
                <th className="text-right px-4 py-2.5">Total</th>
                <th className="text-left px-4 py-2.5">Batches</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="b2b-dispatch-rows">
              {orders.map((o) => (
                <tr key={o.id} className="border-t border-[#EAE5D9]" data-testid={`b2b-dispatch-row-${o.id}`}>
                  <td className="px-4 py-2 font-mono text-xs font-semibold">{o.number}</td>
                  <td className="px-4 py-2">{o.customer_snapshot?.company}</td>
                  <td className="px-4 py-2 font-mono text-xs">{o.customer_po_number}</td>
                  <td className="px-4 py-2 text-right font-mono">{inr(o.grand_total)}</td>
                  <td className="px-4 py-2 text-xs">{(o.linked_batches || []).length} batch(es)</td>
                  <td className="px-4 py-2"><Badge variant="outline" className={`uppercase text-[10px] ${o.status === "dispatched" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-blue-100 text-blue-800 border-blue-200"}`}>{o.status}</Badge></td>
                  <td className="px-4 py-2 text-right space-x-1">
                    <Button size="sm" variant="outline" onClick={() => setShowDispatch(o)} data-testid={`b2b-dispatch-btn-${o.id}`}><Truck className="w-3 h-3 mr-1" /> {o.status === "dispatched" ? "Re-dispatch" : "Dispatch"}</Button>
                    {o.status === "dispatched" && <Button size="sm" variant="outline" onClick={() => dl(o.id)} data-testid={`b2b-dispatch-pdf-${o.id}`}><Download className="w-3 h-3 mr-1" /> Note PDF</Button>}
                  </td>
                </tr>
              ))}
              {orders.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-[#8B7355] italic">No orders awaiting dispatch</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2"><CardTitle className="font-[Outfit] text-base">All Dispatch Notes ({notes.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-xs uppercase text-[#8B7355]"><tr>
              <th className="text-left px-4 py-2">Note #</th><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Client</th><th className="text-left px-4 py-2">Source</th><th className="text-left px-4 py-2">Vehicle</th><th className="text-left px-4 py-2">E-way Bill</th><th className="text-right px-4 py-2">Actions</th>
            </tr></thead>
            <tbody data-testid="b2b-dispatch-notes-rows">
              {notes.map((n) => (
                <tr key={n.id} className="border-t border-[#EAE5D9]" data-testid={`b2b-dispatch-note-row-${n.id}`}>
                  <td className="px-4 py-2 font-mono text-xs font-bold text-[#C05621]">{n.note_number}</td>
                  <td className="px-4 py-2 text-xs">{fmtDate(n.dispatched_at)}</td>
                  <td className="px-4 py-2">{n.customer_snapshot?.company}</td>
                  <td className="px-4 py-2">
                    {n.source === "standalone"
                      ? <Badge className="bg-blue-100 text-blue-800 border-blue-200" variant="outline">Standalone</Badge>
                      : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" variant="outline">Order · {n.source_order_number}</Badge>}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">{n.vehicle_number || "—"}</td>
                  <td className="px-4 py-2 font-mono text-xs">{n.eway_bill_number || "—"}</td>
                  <td className="px-4 py-2 text-right">
                    <Button size="sm" variant="outline" onClick={() => dlNote(n.id)} data-testid={`b2b-dispatch-note-pdf-${n.id}`}><Download className="w-3 h-3 mr-1" /> PDF</Button>
                  </td>
                </tr>
              ))}
              {notes.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8B7355]"><Truck className="w-8 h-8 mx-auto mb-2 opacity-40" />No dispatch notes yet</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showDispatch && <DispatchDialog order={showDispatch} onClose={() => setShowDispatch(null)} onSaved={() => { setShowDispatch(null); load(); }} />}
      {showStandalone && <StandaloneDispatchDialog onClose={() => setShowStandalone(false)} onSaved={() => { setShowStandalone(false); load(); }} />}
    </div>
  );
}

function StandaloneDispatchDialog({ onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [availableBatches, setAvailableBatches] = useState([]);
  const [clientId, setClientId] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [poDate, setPoDate] = useState("");
  const [batches, setBatches] = useState([{ batch_id: "", quantity: 0, product_name: "", expiry_date: "" }]);
  const [info, setInfo] = useState({ vehicle_number: "", driver_name: "", driver_phone: "", transporter: "", eway_bill_number: "", remarks: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/b2b-corporate/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/production/batches").then((r) => setAvailableBatches(Array.isArray(r.data) ? r.data.slice(0, 200) : [])).catch(() => setAvailableBatches([]));
  }, []);

  const upd = (i, k, v) => setBatches((p) => p.map((b, idx) => idx === i ? { ...b, [k]: v } : b));
  const add = () => setBatches((p) => [...p, { batch_id: "", quantity: 0, product_name: "", expiry_date: "" }]);
  const rm = (i) => setBatches((p) => p.filter((_, idx) => idx !== i));
  const pickBatch = (i, bid) => {
    const b = availableBatches.find((x) => x.id === bid || x.batch_id === bid);
    upd(i, "batch_id", bid);
    if (b) { upd(i, "product_id", b.product_id); upd(i, "product_name", b.product_name || b.name); upd(i, "expiry_date", b.expiry_date); }
  };

  const save = async () => {
    if (!clientId) return toast.error("Choose a client");
    const valid = batches.filter((b) => b.batch_id && Number(b.quantity) > 0);
    if (!valid.length) return toast.error("Add at least one batch with quantity");
    setSaving(true);
    try {
      await api.post("/b2b-corporate/dispatch-notes", {
        customer_id: clientId,
        batches: valid,
        ...info,
        customer_po_number: poNumber || null,
        customer_po_date: poDate || null,
      });
      toast.success("Standalone dispatch note created");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">New Standalone Dispatch Note</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Record a dispatch that is NOT linked to a sales order (e.g. sample delivery, replacement, or a physical dispatch you'll invoice later).</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Corporate Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="bg-white" data-testid="b2b-standalone-dispatch-client"><SelectValue placeholder="Pick client" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Customer PO # (optional)</Label><Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="REL/PO/2026/000456" data-testid="b2b-standalone-dispatch-po" /></div>
          <div><Label>PO Date</Label><Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} className="bg-white" /></div>
          <div><Label>Vehicle No. *</Label><Input value={info.vehicle_number} onChange={(e) => setInfo({ ...info, vehicle_number: e.target.value.toUpperCase() })} placeholder="MH-04-KX-9821" data-testid="b2b-standalone-dispatch-vehicle" /></div>
          <div><Label>Driver Name *</Label><Input value={info.driver_name} onChange={(e) => setInfo({ ...info, driver_name: e.target.value })} data-testid="b2b-standalone-dispatch-driver" /></div>
          <div><Label>Driver Phone</Label><Input value={info.driver_phone} onChange={(e) => setInfo({ ...info, driver_phone: e.target.value })} /></div>
          <div><Label>Transporter</Label><Input value={info.transporter} onChange={(e) => setInfo({ ...info, transporter: e.target.value })} data-testid="b2b-standalone-dispatch-transporter" /></div>
          <div className="col-span-2"><Label>E-way Bill Number</Label><Input value={info.eway_bill_number} onChange={(e) => setInfo({ ...info, eway_bill_number: e.target.value })} className="font-mono" data-testid="b2b-standalone-dispatch-eway" /></div>
        </div>
        <div>
          <div className="flex items-center justify-between mt-2 mb-1"><Label>Manufacturing Batches</Label><Button size="sm" variant="outline" onClick={add}><Plus className="w-3 h-3 mr-1" /> Batch</Button></div>
          <div className="space-y-2">
            {batches.map((b, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-6">
                  {availableBatches.length ? (
                    <Select value={b.batch_id} onValueChange={(v) => pickBatch(i, v)}>
                      <SelectTrigger className="bg-white" data-testid={`b2b-standalone-batch-${i}`}><SelectValue placeholder="Pick batch or type ID" /></SelectTrigger>
                      <SelectContent className="bg-white max-h-64">
                        {availableBatches.map((x) => <SelectItem key={x.id} value={x.id}>{x.batch_number || x.batch_id || x.id.slice(0,8)} · {x.product_name || x.name || "-"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input placeholder="Batch ID" value={b.batch_id} onChange={(e) => upd(i, "batch_id", e.target.value)} data-testid={`b2b-standalone-batch-${i}`} />
                  )}
                </div>
                <div className="col-span-3"><Input placeholder="Product name" value={b.product_name || ""} onChange={(e) => upd(i, "product_name", e.target.value)} /></div>
                <div className="col-span-2"><Input type="number" placeholder="Qty" value={b.quantity} onChange={(e) => upd(i, "quantity", Number(e.target.value))} data-testid={`b2b-standalone-qty-${i}`} /></div>
                <div className="col-span-1 text-right"><button onClick={() => rm(i)} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 className="w-3.5 h-3.5" /></button></div>
              </div>
            ))}
          </div>
        </div>
        <div><Label>Remarks</Label><Textarea rows={2} value={info.remarks} onChange={(e) => setInfo({ ...info, remarks: e.target.value })} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="b2b-standalone-dispatch-save">{saving ? "Saving…" : "Create Dispatch Note"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DispatchDialog({ order, onClose, onSaved }) {
  const [batches, setBatches] = useState([{ batch_id: "", quantity: 0, product_name: "", expiry_date: "" }]);
  const [info, setInfo] = useState({ vehicle_number: "", driver_name: "", driver_phone: "", transporter: "", eway_bill_number: "", remarks: "" });
  const [saving, setSaving] = useState(false);
  const [availableBatches, setAvailableBatches] = useState([]);

  useEffect(() => {
    // fetch production batches to help user pick
    api.get("/production/batches").then((r) => setAvailableBatches(Array.isArray(r.data) ? r.data.slice(0, 200) : [])).catch(() => setAvailableBatches([]));
  }, []);

  const upd = (i, k, v) => setBatches((p) => p.map((b, idx) => idx === i ? { ...b, [k]: v } : b));
  const add = () => setBatches((p) => [...p, { batch_id: "", quantity: 0, product_name: "", expiry_date: "" }]);
  const rm = (i) => setBatches((p) => p.filter((_, idx) => idx !== i));

  const pickBatch = (i, bid) => {
    const b = availableBatches.find((x) => x.id === bid || x.batch_id === bid);
    upd(i, "batch_id", bid);
    if (b) {
      upd(i, "product_id", b.product_id);
      upd(i, "product_name", b.product_name || b.name);
      upd(i, "expiry_date", b.expiry_date);
    }
  };

  const save = async () => {
    const valid = batches.filter((b) => b.batch_id && Number(b.quantity) > 0);
    if (!valid.length) return toast.error("Add at least one batch with quantity");
    setSaving(true);
    try {
      await api.post(`/b2b-corporate/orders/${order.id}/dispatch`, { batches: valid, ...info });
      toast.success("Dispatched successfully");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Dispatch failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">Dispatch Order — {order.number}</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Client: {order.customer_snapshot?.company} · PO #{order.customer_po_number}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Vehicle No. *</Label><Input value={info.vehicle_number} onChange={(e) => setInfo({ ...info, vehicle_number: e.target.value.toUpperCase() })} placeholder="MH-04-KX-9821" data-testid="b2b-dispatch-vehicle" /></div>
          <div><Label>Driver Name *</Label><Input value={info.driver_name} onChange={(e) => setInfo({ ...info, driver_name: e.target.value })} data-testid="b2b-dispatch-driver" /></div>
          <div><Label>Driver Phone</Label><Input value={info.driver_phone} onChange={(e) => setInfo({ ...info, driver_phone: e.target.value })} /></div>
          <div><Label>Transporter</Label><Input value={info.transporter} onChange={(e) => setInfo({ ...info, transporter: e.target.value })} placeholder="Delhivery / DTDC / Blue Dart" data-testid="b2b-dispatch-transporter" /></div>
          <div className="col-span-2"><Label>E-way Bill Number</Label><Input value={info.eway_bill_number} onChange={(e) => setInfo({ ...info, eway_bill_number: e.target.value })} className="font-mono" data-testid="b2b-dispatch-eway" /></div>
        </div>
        <div>
          <div className="flex items-center justify-between mt-2 mb-1"><Label>Manufacturing Batches</Label><Button size="sm" variant="outline" onClick={add}><Plus className="w-3 h-3 mr-1" /> Batch</Button></div>
          <div className="space-y-2">
            {batches.map((b, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 items-center">
                <div className="col-span-6">
                  {availableBatches.length ? (
                    <Select value={b.batch_id} onValueChange={(v) => pickBatch(i, v)}>
                      <SelectTrigger className="bg-white" data-testid={`b2b-dispatch-batch-${i}`}><SelectValue placeholder="Pick batch or type ID" /></SelectTrigger>
                      <SelectContent className="bg-white max-h-64">
                        {availableBatches.map((x) => <SelectItem key={x.id} value={x.id}>{x.batch_number || x.batch_id || x.id.slice(0,8)} · {x.product_name || x.name || "-"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input placeholder="Batch ID" value={b.batch_id} onChange={(e) => upd(i, "batch_id", e.target.value)} data-testid={`b2b-dispatch-batch-${i}`} />
                  )}
                </div>
                <div className="col-span-3"><Input placeholder="Product name" value={b.product_name || ""} onChange={(e) => upd(i, "product_name", e.target.value)} /></div>
                <div className="col-span-2"><Input type="number" placeholder="Qty" value={b.quantity} onChange={(e) => upd(i, "quantity", Number(e.target.value))} data-testid={`b2b-dispatch-qty-${i}`} /></div>
                <div className="col-span-1 text-right"><button onClick={() => rm(i)} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 className="w-3.5 h-3.5" /></button></div>
              </div>
            ))}
          </div>
        </div>
        <div><Label>Remarks</Label><Textarea rows={2} value={info.remarks} onChange={(e) => setInfo({ ...info, remarks: e.target.value })} /></div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="b2b-dispatch-save">{saving ? "Saving…" : "Confirm Dispatch"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
 *  4) INVOICES TAB — Convert Order → GST Tax Invoice + PDF
 * ============================================================ */
function InvoicesTab() {
  const [ready, setReady] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showManual, setShowManual] = useState(false);
  const load = async () => {
    try {
      const [os, invs] = await Promise.all([
        api.get("/b2b-corporate/orders", { params: { status: "dispatched" } }),
        api.get("/b2b-corporate/invoices"),
      ]);
      setReady(os.data.filter((o) => !o.invoice_id));
      setInvoices(invs.data);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);

  const convert = async (oid) => {
    try {
      const { data } = await api.post(`/b2b-corporate/orders/${oid}/invoice`);
      toast.success(`Tax Invoice ${data.number} created`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const dl = (iid) => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/b2b-corporate/invoices/${iid}/tax-invoice.pdf`, "_blank");

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#8B7355]">Convert a dispatched order to a Tax Invoice, or create a **manual invoice** (no order needed) — both post to A/R and deduct stock.</p>
        <Button className="bg-[#C05621] hover:bg-[#9C4221] text-white" onClick={() => setShowManual(true)} data-testid="b2b-invoice-manual-add">
          <Plus className="w-4 h-4 mr-1" /> New Manual Invoice
        </Button>
      </div>

      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2"><CardTitle className="font-[Outfit] text-base">Ready to Invoice ({ready.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-xs uppercase text-[#8B7355]"><tr>
              <th className="text-left px-4 py-2">Order #</th><th className="text-left px-4 py-2">Client</th><th className="text-left px-4 py-2">Customer PO</th><th className="text-right px-4 py-2">Amount</th><th className="text-right px-4 py-2">Action</th>
            </tr></thead>
            <tbody data-testid="b2b-invoice-ready">
              {ready.map((o) => (
                <tr key={o.id} className="border-t border-[#EAE5D9]">
                  <td className="px-4 py-2 font-mono text-xs font-semibold">{o.number}</td>
                  <td className="px-4 py-2">{o.customer_snapshot?.company}</td>
                  <td className="px-4 py-2 font-mono text-xs">{o.customer_po_number}</td>
                  <td className="px-4 py-2 text-right font-mono">{inr(o.grand_total)}</td>
                  <td className="px-4 py-2 text-right"><Button size="sm" className="bg-[#C05621] hover:bg-[#9C4221] text-white" onClick={() => convert(o.id)} data-testid={`b2b-invoice-convert-${o.id}`}><ArrowRight className="w-3 h-3 mr-1" /> Generate Tax Invoice</Button></td>
                </tr>
              ))}
              {ready.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-[#8B7355] italic">No dispatched orders awaiting invoicing</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card className="border-[#EAE5D9]">
        <CardHeader className="pb-2"><CardTitle className="font-[Outfit] text-base">All B2B Tax Invoices ({invoices.length})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF6EE] text-xs uppercase text-[#8B7355]"><tr>
              <th className="text-left px-4 py-2">Invoice #</th><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Client</th><th className="text-left px-4 py-2">Customer PO</th><th className="text-left px-4 py-2">Source</th><th className="text-right px-4 py-2">Amount</th><th className="text-left px-4 py-2">Payment</th><th className="text-right px-4 py-2">Actions</th>
            </tr></thead>
            <tbody data-testid="b2b-invoice-rows">
              {invoices.map((i) => (
                <tr key={i.id} className="border-t border-[#EAE5D9]" data-testid={`b2b-invoice-row-${i.id}`}>
                  <td className="px-4 py-2 font-mono text-xs font-bold text-[#C05621]">{i.number}</td>
                  <td className="px-4 py-2 text-xs">{fmtDate(i.created_at)}</td>
                  <td className="px-4 py-2">{i.customer_snapshot?.company}</td>
                  <td className="px-4 py-2 font-mono text-xs">{i.customer_po_number}</td>
                  <td className="px-4 py-2">
                    {i.source === "manual" || !i.source_order_id
                      ? <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-200 uppercase text-[10px]">Manual</Badge>
                      : <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-200 uppercase text-[10px]">{i.source_order_number}</Badge>}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{inr(i.grand_total)}</td>
                  <td className="px-4 py-2"><Badge variant="outline" className={`uppercase text-[10px] ${i.payment_status === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : "bg-red-100 text-red-800 border-red-200"}`}>{i.payment_status || "unpaid"}</Badge></td>
                  <td className="px-4 py-2 text-right"><Button size="sm" variant="outline" onClick={() => dl(i.id)} data-testid={`b2b-invoice-pdf-${i.id}`}><Download className="w-3 h-3 mr-1" /> Tax Invoice PDF</Button></td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-[#8B7355]"><FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />No invoices yet</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {showManual && <ManualInvoiceDialog onClose={() => setShowManual(false)} onSaved={() => { setShowManual(false); load(); }} />}
    </div>
  );
}

function ManualInvoiceDialog({ onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [pl, setPl] = useState([]);
  const [items, setItems] = useState([]);
  const [poNumber, setPoNumber] = useState("");
  const [poDate, setPoDate] = useState("");
  const [interstate, setInterstate] = useState(false);
  const [deductStock, setDeductStock] = useState(true);
  const [terms, setTerms] = useState({ delivery: "", payment: "Net 15" });
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.get("/b2b-corporate/clients").then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    if (!clientId) { setPl([]); return; }
    api.get(`/b2b-corporate/clients/${clientId}/price-list`).then((r) => setPl(r.data.items)).catch(() => setPl([]));
    setItems([]);
  }, [clientId]);

  const addItem = (pid) => {
    const p = pl.find((x) => x.product_id === pid);
    if (!p) return;
    if (items.find((it) => it.product_id === pid)) return toast.error("Already added");
    setItems((prev) => [...prev, { product_id: p.product_id, name: p.name, quantity: 1, unit: p.unit || "pcs", unit_price: p.effective_price, gst_rate: p.gst_rate, hsn: p.hsn }]);
  };
  const upd = (i, k, v) => setItems((prev) => prev.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const rm = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const total = useMemo(() => items.reduce((s, it) => {
    const g = Number(it.quantity || 0) * Number(it.unit_price || 0);
    return s + g + (g * (Number(it.gst_rate || 0) / 100));
  }, 0), [items]);

  const save = async () => {
    if (!clientId) return toast.error("Choose a client");
    if (!poNumber.trim()) return toast.error("Customer PO Number is required");
    if (!items.length) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const { data } = await api.post("/b2b-corporate/invoices", {
        customer_id: clientId, customer_po_number: poNumber.trim(),
        customer_po_date: poDate || null, is_interstate: interstate,
        delivery_terms: terms.delivery, payment_terms: terms.payment,
        notes, items, deduct_stock: deductStock,
      });
      toast.success(`Tax Invoice ${data.number} created`);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-white max-w-4xl">
        <DialogHeader>
          <DialogTitle className="font-[Outfit]">New Manual B2B Tax Invoice</DialogTitle>
          <DialogDescription className="text-xs text-[#8B7355]">Create a GST tax invoice directly (no sales order needed). Auto-posts to Accounts Receivable and deducts stock.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <Label>Corporate Client *</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger className="bg-white" data-testid="b2b-manual-invoice-client"><SelectValue placeholder="Pick client" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Customer PO # *</Label><Input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} placeholder="REL/PO/2026/000456" data-testid="b2b-manual-invoice-po" /></div>
          <div><Label>PO Date</Label><Input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} className="bg-white" /></div>
          <div><Label>Delivery Terms</Label><Input value={terms.delivery} onChange={(e) => setTerms({ ...terms, delivery: e.target.value })} placeholder="FOR Warehouse Mumbai" /></div>
          <div><Label>Payment Terms</Label><Input value={terms.payment} onChange={(e) => setTerms({ ...terms, payment: e.target.value })} /></div>
          <div className="flex items-end gap-4 pb-1">
            <div className="flex items-center gap-2"><Switch checked={interstate} onCheckedChange={setInterstate} id="mints" /><Label htmlFor="mints" className="text-sm">Interstate (IGST)</Label></div>
            <div className="flex items-center gap-2"><Switch checked={deductStock} onCheckedChange={setDeductStock} id="mstock" /><Label htmlFor="mstock" className="text-sm">Deduct stock</Label></div>
          </div>
        </div>
        {clientId && (
          <div className="mt-2">
            <Label>Add Line Items</Label>
            <Select value="" onValueChange={addItem}>
              <SelectTrigger className="bg-white" data-testid="b2b-manual-invoice-add-item"><SelectValue placeholder="Search & add product…" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {pl.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name} · ₹{p.effective_price}{p.has_override ? " (bulk)" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="mt-2 border border-[#EAE5D9] rounded max-h-56 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF6EE] text-xs uppercase text-[#8B7355] sticky top-0">
                  <tr><th className="text-left px-2 py-1.5">Product</th><th className="text-right px-2 py-1.5">Qty</th><th className="text-right px-2 py-1.5">Rate</th><th className="text-right px-2 py-1.5">GST %</th><th className="text-right px-2 py-1.5">Amount</th><th></th></tr>
                </thead>
                <tbody data-testid="b2b-manual-invoice-items">
                  {items.map((it, i) => (
                    <tr key={i} className="border-t border-[#EAE5D9]">
                      <td className="px-2 py-1">{it.name}</td>
                      <td className="px-2 py-1"><Input type="number" min="0" step="0.01" value={it.quantity} onChange={(e) => upd(i, "quantity", Number(e.target.value))} className="w-20 h-7 text-right ml-auto" /></td>
                      <td className="px-2 py-1"><Input type="number" min="0" step="0.01" value={it.unit_price} onChange={(e) => upd(i, "unit_price", Number(e.target.value))} className="w-24 h-7 text-right ml-auto" /></td>
                      <td className="px-2 py-1"><Input type="number" min="0" step="0.1" value={it.gst_rate} onChange={(e) => upd(i, "gst_rate", Number(e.target.value))} className="w-16 h-7 text-right ml-auto" /></td>
                      <td className="px-2 py-1 text-right font-mono">{inr(Number(it.quantity) * Number(it.unit_price) * (1 + Number(it.gst_rate) / 100))}</td>
                      <td className="px-2 py-1 text-right"><button onClick={() => rm(i)} className="text-red-600 hover:bg-red-50 rounded p-1"><Trash2 className="w-3.5 h-3.5" /></button></td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td colSpan={6} className="text-center py-4 text-[#8B7355] italic text-xs">No items</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <div className="mt-2"><Label>Notes</Label><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></div>
        <div className="pt-2 flex items-center justify-between border-t border-[#EAE5D9] mt-2">
          <span className="text-sm text-[#8B7355]">Estimated Total (incl. GST):</span>
          <span className="text-2xl font-[Outfit] font-bold text-[#C05621]">{inr(total)}</span>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#C05621] hover:bg-[#9C4221] text-white" data-testid="b2b-manual-invoice-save">
            {saving ? "Saving…" : "Create Tax Invoice"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
 *  5) CLIENT LEDGER TAB
 * ============================================================ */
function LedgerTab() {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => { api.get("/b2b-corporate/clients").then((r) => setClients(r.data)); }, []);
  useEffect(() => {
    if (!clientId) { setData(null); return; }
    api.get(`/b2b-corporate/clients/${clientId}/ledger`).then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  }, [clientId]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <Label className="text-xs">Choose Client:</Label>
        <Select value={clientId} onValueChange={setClientId}>
          <SelectTrigger className="w-96 bg-white" data-testid="b2b-ledger-client"><SelectValue placeholder="Pick a corporate client to view A/R" /></SelectTrigger>
          <SelectContent className="bg-white max-h-72">
            {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Total Invoiced" value={inr(data.totals.invoiced)} tone="blue" />
            <Kpi label="Total Received" value={inr(data.totals.paid)} tone="green" />
            <Kpi label="Outstanding (A/R)" value={inr(data.totals.outstanding)} tone={data.totals.outstanding > 0 ? "red" : "green"} bold />
          </div>
          <Card className="border-[#EAE5D9]">
            <CardHeader className="pb-2"><CardTitle className="font-[Outfit] text-base">Invoice History — {data.client.company || data.client.name}</CardTitle></CardHeader>
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead className="bg-[#FAF6EE] text-xs uppercase text-[#8B7355]"><tr>
                  <th className="text-left px-4 py-2">Invoice #</th><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Customer PO</th><th className="text-right px-4 py-2">Total</th><th className="text-right px-4 py-2">Paid</th><th className="text-right px-4 py-2">Balance</th><th className="text-left px-4 py-2">Status</th>
                </tr></thead>
                <tbody data-testid="b2b-ledger-rows">
                  {data.invoices.map((i) => (
                    <tr key={i.invoice_id} className="border-t border-[#EAE5D9]">
                      <td className="px-4 py-2 font-mono text-xs font-semibold">{i.invoice_number}</td>
                      <td className="px-4 py-2 text-xs">{fmtDate(i.invoice_date)}</td>
                      <td className="px-4 py-2 font-mono text-xs">{i.customer_po_number}</td>
                      <td className="px-4 py-2 text-right font-mono">{inr(i.grand_total)}</td>
                      <td className="px-4 py-2 text-right font-mono text-emerald-700">{inr(i.amount_paid)}</td>
                      <td className={`px-4 py-2 text-right font-mono font-bold ${i.balance_due > 0 ? "text-red-700" : "text-emerald-700"}`}>{inr(i.balance_due)}</td>
                      <td className="px-4 py-2"><Badge variant="outline" className={`uppercase text-[10px] ${i.payment_status === "paid" ? "bg-emerald-100 text-emerald-800 border-emerald-200" : i.payment_status === "partial" ? "bg-amber-100 text-amber-800 border-amber-200" : "bg-red-100 text-red-800 border-red-200"}`}>{i.payment_status}</Badge></td>
                    </tr>
                  ))}
                  {data.invoices.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8B7355]">No invoices for this client</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </>
      )}
      {!clientId && <div className="text-sm text-[#8B7355] italic p-6 text-center border border-dashed border-[#EAE5D9] rounded"><BookOpen className="w-10 h-10 mx-auto opacity-40 mb-2" />Select a corporate client above to view their A/R ledger</div>}
    </div>
  );
}

const Kpi = ({ label, value, tone, bold }) => {
  const map = {
    blue: "bg-blue-50 border-blue-200 text-blue-800",
    green: "bg-emerald-50 border-emerald-200 text-emerald-800",
    red: "bg-red-50 border-red-200 text-red-800",
  };
  return (
    <div className={`rounded-lg border p-4 ${map[tone] || ""}`}>
      <div className="text-xs uppercase tracking-wider opacity-80">{label}</div>
      <div className={`font-[Outfit] mt-1 ${bold ? "text-3xl font-bold" : "text-2xl font-semibold"}`}>{value}</div>
    </div>
  );
};
